import { clerkPortraitImageUrl } from "./MorrowPersonCard";
import {
  defaultMorrowDicebearAvatarSeed,
  normalizeMorrowDicebearAvatarSeed,
  resolvePersonAvatarUrl,
} from "./morrowPersonAvatarCatalog";
import type { MorrowUserProfile } from "./morrowUserProfileStore";

type ClerkPublicUser = {
  firstName?: string | null;
  lastName?: string | null;
  identifier?: string | null;
  userId?: string | null;
  hasImage?: boolean;
  imageUrl?: string | null;
};

type ClerkMembershipEntry = {
  publicUserData?: ClerkPublicUser | null;
};

type ClerkUser = {
  id?: string;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  primaryEmailAddress?: { emailAddress?: string | null } | null;
  createdAt?: Date | null;
  hasImage?: boolean;
  imageUrl?: string | null;
};

function rememberClerkPortrait(
  map: Map<string, string>,
  key: string | undefined | null,
  imageUrl: string,
) {
  const normalized = key?.trim().toLowerCase();
  if (normalized) map.set(normalized, imageUrl);
}

/** Clerk membership portraits keyed by email, user id, and display name. */
export function buildClerkMemberAvatarMap(
  memberships: readonly ClerkMembershipEntry[] | undefined,
  user: ClerkUser | null | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const entry of memberships ?? []) {
    const imageUrl = clerkPortraitImageUrl(entry.publicUserData);
    if (!imageUrl) continue;
    const publicUser = entry.publicUserData;
    rememberClerkPortrait(map, publicUser?.identifier, imageUrl);
    rememberClerkPortrait(map, publicUser?.userId, imageUrl);
    rememberClerkPortrait(
      map,
      [publicUser?.firstName, publicUser?.lastName].filter(Boolean).join(" "),
      imageUrl,
    );
  }
  const selfImage = clerkPortraitImageUrl(user);
  if (selfImage) {
    rememberClerkPortrait(map, user?.primaryEmailAddress?.emailAddress, selfImage);
    rememberClerkPortrait(map, user?.id, selfImage);
    rememberClerkPortrait(map, user?.fullName, selfImage);
    rememberClerkPortrait(
      map,
      [user?.firstName, user?.lastName].filter(Boolean).join(" "),
      selfImage,
    );
  }
  return map;
}

export function resolveClerkPortrait(
  map: ReadonlyMap<string, string> | undefined,
  key: string | undefined | null,
): string | undefined {
  if (!map || !key) return undefined;
  return map.get(key.trim().toLowerCase());
}

export function isSelfMember(
  member: { userId?: string | null; email?: string },
  selfUserId: string | null | undefined,
  selfEmail: string | undefined,
): boolean {
  if (selfUserId != null && member.userId === selfUserId) return true;
  const memberEmail = member.email?.trim().toLowerCase();
  const normalizedSelfEmail = selfEmail?.trim().toLowerCase();
  return Boolean(memberEmail && normalizedSelfEmail && memberEmail === normalizedSelfEmail);
}

export type ResolvedMemberPersonAvatar = {
  readonly imageUrl?: string;
  readonly avatarSeed?: string;
};

export function resolveSelfAvatarSeed(
  storedSeed: string | undefined,
  fallbackName: string,
): string {
  return (
    normalizeMorrowDicebearAvatarSeed(storedSeed) ?? defaultMorrowDicebearAvatarSeed(fallbackName)
  );
}

/** Shared portrait resolution: Clerk photo when available, otherwise DiceBear seed. */
export function resolveMemberPersonAvatar(input: {
  readonly email?: string;
  readonly userId?: string | null;
  readonly name?: string;
  readonly avatarSeed?: string;
  readonly clerkPortraitMap?: ReadonlyMap<string, string>;
  readonly resolveClerkPortrait?: (key: string) => string | undefined;
  readonly isSelf?: boolean;
  readonly selfAvatarSeed?: string;
  readonly selfPortraitImageUrl?: string;
}): ResolvedMemberPersonAvatar {
  const isSelf = input.isSelf === true;
  const lookup = (key: string | undefined | null) =>
    (key ? input.resolveClerkPortrait?.(key) : undefined) ??
    resolveClerkPortrait(input.clerkPortraitMap, key);

  const selfPortrait = input.selfPortraitImageUrl?.trim();
  const imageUrl = isSelf
    ? selfPortrait || undefined
    : (lookup(input.email) ?? lookup(input.userId) ?? lookup(input.name));

  const seed = isSelf
    ? input.selfAvatarSeed?.trim() || undefined
    : input.avatarSeed?.trim() || undefined;

  return {
    ...(imageUrl ? { imageUrl } : {}),
    ...(seed ? { avatarSeed: seed } : {}),
  };
}

export type PersonalIdentity = {
  readonly personName: string;
  readonly personEmail: string;
  readonly memberSince: Date | null;
  readonly title: string;
  readonly role: string;
  readonly avatar: ResolvedMemberPersonAvatar;
};

/** Signed-in user's identity card fields — DiceBear avatar, not Clerk portrait. */
export function buildPersonalIdentity(input: {
  readonly fallbackName: string;
  readonly profile: MorrowUserProfile;
  readonly user?: ClerkUser | null | undefined;
}): PersonalIdentity {
  const personName =
    input.user?.fullName?.trim() ||
    input.user?.firstName?.trim() ||
    input.user?.primaryEmailAddress?.emailAddress ||
    input.fallbackName;
  const personEmail = input.user?.primaryEmailAddress?.emailAddress?.trim() ?? "";
  const memberSince = input.user?.createdAt ? new Date(input.user.createdAt) : null;
  const avatar = resolveMemberPersonAvatar({
    name: personName,
    isSelf: true,
    selfAvatarSeed: resolveSelfAvatarSeed(input.profile.avatarSeed, personName),
    selfPortraitImageUrl: input.profile.portraitImageUrl,
  });

  return {
    personName,
    personEmail,
    memberSince,
    title: input.profile.title,
    role: input.profile.role,
    avatar,
  };
}

export function personalIdentityAvatarUrl(
  name: string,
  avatar: ResolvedMemberPersonAvatar,
): string {
  return resolvePersonAvatarUrl(name, {
    ...(avatar.imageUrl ? { imageUrl: avatar.imageUrl } : {}),
    ...(avatar.avatarSeed ? { avatarSeed: avatar.avatarSeed } : {}),
  });
}
